import axios from "axios";
import { tokenManager } from "../utils/tokenManager";

const API_BASE_URL =
  window.env.REACT_APP_API_BASE_URL || "https://api.omnimize.poc.omniops.sa";
const OMNIMIZER_PATH = window.env.REACT_APP_OMNIMIZER_PATH || "/omnimizer-api";

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Setup secure interceptors using centralized token manager
tokenManager.setupInterceptors(api);

// Deployments API
export const deploymentApi = {
  // Create a new deployment
  createDeployment: (deploymentData) => {
    return api.post(`${OMNIMIZER_PATH}/deployments/`, deploymentData);
  },

  // List all deployments
  listDeployments: (params = {}) => {
    return api.get(`${OMNIMIZER_PATH}/deployments/`, { params });
  },

  // Get deployment by ID
  getDeployment: (deploymentId, version = null) => {
    const params = version ? { version } : {};
    return api.get(`${OMNIMIZER_PATH}/deployments/${deploymentId}`, { params });
  },

  // Get accessible deployments with API keys
  getAccessibleDeployments: () => {
    return api.get(`${OMNIMIZER_PATH}/deployments/access`);
  },

  // Delete deployment
  deleteDeployment: (deploymentId) => {
    return api.delete(`${OMNIMIZER_PATH}/deployments/${deploymentId}`);
  },

  // Get task status
  getTaskStatus: (taskId) => {
    return api.get(`${OMNIMIZER_PATH}/deployments/tasks/${taskId}`);
  },

  // Get deployment timeline
  getDeploymentTimeline: (deploymentId, version = null, limit = 100) => {
    const params = version ? { version, limit } : { limit };
    return api.get(`${OMNIMIZER_PATH}/deployments/${deploymentId}/timeline`, {
      params,
    });
  },

  // Create new version
  createNewVersion: async (modelId, deploymentData) => {
    return await api.post(
      `${OMNIMIZER_PATH}/deployments/${modelId}/versions`,
      deploymentData
    );
  },

  // Activate version
  activateVersion: async (modelId, version) => {
    return await api.post(
      `${OMNIMIZER_PATH}/deployments/${modelId}/versions/${version}/activate`
    );
  },

  // Get all versions of a deployment
  getDeploymentVersions: async (modelId) => {
    return await api.get(`${OMNIMIZER_PATH}/deployments/${modelId}/versions`);
  },

  getPredefinedDeployments: (activeOnly = true) => {
    return api.get(
      `${OMNIMIZER_PATH}/predefined-deployments/?active_only=${activeOnly}`
    );
  },

  // Create a predefined deployment (admin only)
  createPredefinedDeployment: (deploymentData) => {
    return api.post(
      `${OMNIMIZER_PATH}/predefined-deployments/`,
      deploymentData
    );
  },

  // Update a predefined deployment (admin only)
  updatePredefinedDeployment: (deploymentId, deploymentData) => {
    return api.put(
      `${OMNIMIZER_PATH}/predefined-deployments/${deploymentId}`,
      deploymentData
    );
  },

  /**
   * Delete a predefined deployment (admin only)
   * @param {number|string} deploymentId - The ID of the predefined deployment to delete
   * @returns {Promise<Object>} - Promise with response data
   */
  deletePredefinedDeployment: (deploymentId) => {
    // Delete predefined deployment called with ID

    if (deploymentId === undefined || deploymentId === null) {
      // Deployment ID is null or undefined
      return Promise.reject(new Error("Deployment ID is required"));
    }

    // Ensure deploymentId is treated as an integer
    const id = parseInt(deploymentId, 10);

    // Check if ID is valid
    if (isNaN(id)) {
      // Invalid deployment ID format
      return Promise.reject(
        new Error(`Invalid deployment ID format: ${deploymentId}`)
      );
    }

    // Sending delete request
    return api.delete(`${OMNIMIZER_PATH}/predefined-deployments/${id}`);
  },
};

// API Keys API
export const apiKeysApi = {
  // Create API key
  createApiKey: (
    deploymentId,
    userId,
    description = null,
    expiresInDays = null
  ) => {
    const params = { user_id: userId };
    if (description) params.description = description;
    if (expiresInDays) params.expires_in_days = expiresInDays;

    return api.post(
      `${OMNIMIZER_PATH}/deployments/${deploymentId}/keys`,
      null,
      { params }
    );
  },

  // List API keys
  listApiKeys: (deploymentId, userId) => {
    return api.get(`${OMNIMIZER_PATH}/deployments/${deploymentId}/keys`, {
      params: { user_id: userId },
    });
  },

  // Get a specific API key by ID
  getApiKey: (deploymentId, keyId) => {
    return api.get(
      `${OMNIMIZER_PATH}/deployments/${deploymentId}/keys/${keyId}`
    );
  },

  // Delete API key
  deleteApiKey: (deploymentId, keyId, userId) => {
    return api.delete(
      `${OMNIMIZER_PATH}/deployments/${deploymentId}/keys/${keyId}`,
      {
        params: { user_id: userId },
      }
    );
  },

  // Update API key
  updateApiKey: (
    deploymentId,
    keyId,
    userId,
    description = null,
    expiresInDays = null
  ) => {
    const params = { user_id: userId };
    if (description) params.description = description;
    if (expiresInDays) params.expires_in_days = expiresInDays;

    return api.patch(
      `${OMNIMIZER_PATH}/deployments/${deploymentId}/keys/${keyId}`,
      null,
      { params }
    );
  },
};

// Credentials API
export const credentialsApi = {
  // Get credentials with optional source filter
  getCredentials: (source = null, skip = 0, limit = 100) => {
    const params = { skip, limit };
    if (source) params.source = source;

    return api.get(`${OMNIMIZER_PATH}/credentials/`, { params });
  },

  // Create new credentials
  createCredentials: (credentialsData) => {
    return api.post(`${OMNIMIZER_PATH}/credentials/`, credentialsData);
  },
};

// Usage API service
export const usageApi = {
  // Create a function to convert filters to queryParams to avoid code duplication
  _createUsageQueryParams: (filters = {}) => {
    const queryParams = new URLSearchParams();

    // Add all provided filters to query params
    if (filters.userId) queryParams.append("user_id", filters.userId);
    if (filters.userEmail) queryParams.append("user_email", filters.userEmail);
    if (filters.deploymentId)
      queryParams.append("deployment_id", filters.deploymentId);
    if (filters.status) queryParams.append("status", filters.status);
    if (filters.startDate)
      queryParams.append("start_date", filters.startDate.toISOString());
    if (filters.endDate)
      queryParams.append("end_date", filters.endDate.toISOString());

    // Add project_id as array parameter if it exists
    if (filters.projectIds && Array.isArray(filters.projectIds)) {
      filters.projectIds.forEach((id) => queryParams.append("project_id", id));
    }

    // Only add pagination if specified
    if (filters.pageSize) queryParams.append("page_size", filters.pageSize);
    if (typeof filters.page === "number")
      queryParams.append("page", filters.page);

    return queryParams;
  },

  getUsageRecords: async (filters = {}) => {
    const queryParams = usageApi._createUsageQueryParams(filters);

    // Add default pagination if not provided
    if (!filters.pageSize) queryParams.append("page_size", "50");
    if (typeof filters.page !== "number") queryParams.append("page", "1");

    return api.get(`${OMNIMIZER_PATH}/usage/records`, { params: queryParams });
  },

  getUsageSummary: async (filters = {}) => {
    const queryParams = usageApi._createUsageQueryParams(filters);
    return api.get(`${OMNIMIZER_PATH}/usage/summary`, { params: queryParams });
  },

  getOrganizationQuota: async (
    organizationId,
    resourceType = "GPU_HOURS"
  ) => {
    return api.get(`${OMNIMIZER_PATH}/usage/${organizationId}/quota`, {
      params: { resource_type: resourceType },
    });
  },

  getCurrentUsageSummary: async (
    userId = null,
    userEmail = null,
    projectIds = null
  ) => {
    const params = {};
    if (userId) params.user_id = userId;
    if (userEmail) params.user_email = userEmail;

    // Add project_id as array parameter if it exists
    if (projectIds && Array.isArray(projectIds)) {
      params.project_id = projectIds;
    }

    return api.get(`${OMNIMIZER_PATH}/usage/summary/current`, { params });
  },

  getUserInvoices: async (userId = null, status = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    if (status) params.status = status;

    return api.get(`${OMNIMIZER_PATH}/usage/invoices`, { params });
  },

  getGpuRates: async () => {
    return api.get(`${OMNIMIZER_PATH}/usage/rates`);
  },

  getPaymentMethods: async (userId) => {
    return api.get(`${OMNIMIZER_PATH}/usage/payment-methods`, {
      params: { user_id: userId },
    });
  },

  deletePaymentMethod: async (userId, paymentMethodId) => {
    return api.delete(
      `${OMNIMIZER_PATH}/usage/payment-methods/${paymentMethodId}`,
      {
        params: { user_id: userId },
      }
    );
  },

  getGpuRate: (gpuType) => {
    return api.get(`${OMNIMIZER_PATH}/usage/rates/${gpuType}`);
  },

  createGpuRate: (rateData) => {
    return api.post(`${OMNIMIZER_PATH}/usage/rates`, rateData);
  },

  updateGpuRate: (gpuType, rateData) => {
    return api.put(`${OMNIMIZER_PATH}/usage/rates/${gpuType}`, rateData);
  },

  deleteGpuRate: (gpuType) => {
    return api.delete(`${OMNIMIZER_PATH}/usage/rates/${gpuType}`);
  },

  startUsage: (deploymentId, userId, version = null) => {
    const params = { user_id: userId };
    if (version) params.version = version;

    return api.post(`${OMNIMIZER_PATH}/usage/start/${deploymentId}`, null, {
      params,
    });
  },

  stopUsage: (usageId) => {
    return api.post(`${OMNIMIZER_PATH}/usage/stop/${usageId}`);
  },

  autoTrackDeployment: (deploymentId) => {
    return api.post(`${OMNIMIZER_PATH}/usage/track/auto/${deploymentId}`);
  },

  generateUserInvoice: (userId) => {
    return api.post(`${OMNIMIZER_PATH}/usage/invoices/generate`, null, {
      params: { user_id: userId },
    });
  },

  markInvoicePaid: (invoiceId) => {
    return api.post(`${OMNIMIZER_PATH}/usage/invoices/${invoiceId}/mark-paid`);
  },

  getInvoiceDetails: (invoiceId) => {
    return api.get(`${OMNIMIZER_PATH}/usage/invoices/${invoiceId}/details`);
  },
};

// Collection endpoints
export const collectionApi = {
  getCollections: () => {
    return api.get(`${OMNIMIZER_PATH}/collections`);
  },

  getCollection: (id) => {
    return api.get(`${OMNIMIZER_PATH}/collections/${id}`);
  },

  createCollection: (data) => {
    return api.post(`${OMNIMIZER_PATH}/collections`, data);
  },

  updateCollection: (id, data) => {
    return api.put(`${OMNIMIZER_PATH}/collections/${id}`, data);
  },

  deleteCollection: (id) => {
    return api.delete(`${OMNIMIZER_PATH}/collections/${id}`);
  },
};

// Document endpoints
export const documentApi = {
  getDocuments: (collectionId) => {
    return api.get(`${OMNIMIZER_PATH}/collections/${collectionId}/documents`);
  },

  getDocument: (collectionId, documentId) => {
    return api.get(
      `${OMNIMIZER_PATH}/collections/${collectionId}/documents/${documentId}`
    );
  },

  uploadDocument: (collectionId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(
      `${OMNIMIZER_PATH}/collections/${collectionId}/documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },

  deleteDocument: (collectionId, documentId) => {
    return api.delete(
      `${OMNIMIZER_PATH}/collections/${collectionId}/documents/${documentId}`
    );
  },
};

// Chat endpoints
export const chatApi = {
  sendMessage: (collectionId, message) => {
    return api.post(`${OMNIMIZER_PATH}/collections/${collectionId}/chat`, {
      message,
    });
  },
};

// User endpoints
export const userApi = {
  getUserProfile: () => {
    return api.get(`${OMNIMIZER_PATH}/user/profile`);
  },

  updateUserProfile: (data) => {
    return api.put(`${OMNIMIZER_PATH}/user/profile`, data);
  },
};

// Scheduled Uptime API
export const scheduledUptimeApi = {
  // Create a new schedule
  createSchedule: (scheduleData) => {
    return api.post(`${OMNIMIZER_PATH}/scheduled-uptime/`, scheduleData);
  },

  // List schedules for a deployment
  listSchedules: (deploymentId, includeDeleted = false) => {
    const params = {
      deployment_id: deploymentId,
      include_deleted: includeDeleted,
    };
    return api.get(`${OMNIMIZER_PATH}/scheduled-uptime/`, { params });
  },

  // Get a specific schedule
  getSchedule: (scheduleId) => {
    return api.get(`${OMNIMIZER_PATH}/scheduled-uptime/${scheduleId}`);
  },

  // Update an existing schedule
  updateSchedule: (scheduleId, scheduleData) => {
    return api.put(
      `${OMNIMIZER_PATH}/scheduled-uptime/${scheduleId}`,
      scheduleData
    );
  },

  // Delete a schedule
  deleteSchedule: (scheduleId) => {
    return api.delete(`${OMNIMIZER_PATH}/scheduled-uptime/${scheduleId}`);
  },
};

export default api;
